import { InputProps as ChakraInputProps, Input } from '@chakra-ui/react';
import { default as EyeIcon } from '~icons/tabler/eye';
import { default as EyeOffIcon } from '~icons/tabler/eye-off';
import { RefCallback, useCallback, useEffect, useRef, useState } from 'react';
import { FieldValues, UseControllerProps } from 'react-hook-form';

import { useField } from '#features/form/hooks/useField';
import { IconButton } from '../../IconButton';
import { BaseInput } from '../BaseInput';
import { InputGroup } from '../InputGroup';
import { toDomProps } from '../splitFieldProps';
import { InputProps } from '../types';

type InputComponentProps = InputProps<string> &
  Omit<ChakraInputProps, keyof InputProps<string>> & {
    ref?: RefCallback<HTMLInputElement>;
  };

const InputComponent = ({ value = '', onChange, ...props }: InputComponentProps) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const changePasswordVisibility = useCallback(() => {
    setIsPasswordVisible((currentState) => !currentState);
  }, []);

  // Поле отдаёт наружу строку, как и текстовое: форма хранит пароль, а не событие.
  const handleChange = ({ target: { value: _value } }: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(_value);
  };

  /*
   * Подставленный браузером пароль нужно забрать в форму руками.
   *
   * Автозаполнение кладёт значение прямо в DOM и не шлёт события, которое слышит React: в
   * поле текст есть, в состоянии формы пусто. Человек видит заполненную форму, жмёт «Войти»,
   * и уходит пустой пароль - бэк отвечает «Invalid email or password» на верной паре.
   *
   * Забрать значение по перерисовке нельзя: если Chrome подставил пароль до монтирования и
   * больше ничего на странице не менялось, перерисовки не будет вовсе, и поле так и уедет
   * пустым. Поэтому читаем DOM в моменты, когда значение там уже точно лежит: сразу после
   * монтирования, по анимации-метке автозаполнения и перед самой отправкой формы.
   */
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const syncFromDom = useCallback(() => {
    const domValue = inputRef.current?.value;
    if (domValue && domValue !== valueRef.current) {
      onChangeRef.current?.(domValue);
    }
  }, []);

  useEffect(() => {
    // Chrome подставляет сохранённую пару не мгновенно: между монтированием и подстановкой
    // проходит кадр-другой, а иногда она случается только после того, как человек выбрал
    // аккаунт в выпадашке менеджера паролей. Одной проверки на монтировании не хватает.
    syncFromDom();

    const input = inputRef.current;
    if (!input) return;

    const form = input.form;
    // Последняя линия обороны: что бы ни случилось с событиями выше, перед отправкой
    // состояние формы сверяется с тем, что реально лежит в поле.
    form?.addEventListener('submit', syncFromDom, { capture: true });

    return () => {
      form?.removeEventListener('submit', syncFromDom, { capture: true });
    };
  }, [syncFromDom]);

  const inputType = isPasswordVisible ? 'text' : 'password';

  return (
    <InputGroup
      w="100%"
      endElement={
        props.disabled ? null : (
          <IconButton
            tabIndex={-1}
            me="-2"
            aspectRatio="square"
            size="sm"
            variant="ghost"
            height="calc(100% - {spacing.2})"
            aria-label="Toggle password visibility"
            onClick={changePasswordVisibility}
          >
            {isPasswordVisible ? <EyeOffIcon /> : <EyeIcon />}
          </IconButton>
        )
      }
    >
      <Input
        pr="4.5rem"
        {...{
          ...toDomProps(props),
          ref: inputRef,
          value,
          onChange: handleChange,
          onAnimationStart: syncFromDom,
          type: inputType,
        }}
      />
    </InputGroup>
  );
};

export const PasswordInput = ({ ref, ...props }: InputComponentProps) => {
  return (
    <BaseInput {...props}>
      <InputComponent ref={ref} {...props} />
    </BaseInput>
  );
};

export type PasswordInputFieldProps<T extends FieldValues> = UseControllerProps<T> &
  Omit<InputComponentProps, 'value' | 'defaultValue' | 'ref'>;

export const PasswordInputField = <T extends FieldValues>({
  name,
  control,
  defaultValue = '' as never,
  onChange,
  ...props
}: PasswordInputFieldProps<T>) => {
  const fieldProps = useField({
    control,
    defaultValue,
    name,
    onChange,
  });

  return <PasswordInput {...fieldProps} {...props} />;
};
